import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';

import { AtomMapUiPreferences } from '@atom/types/map';

import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBRARIES } from './mapConfig';
import { MapStateAction, mapStateReducer } from './mapStateReducer';
import {
  atomMapStateExists,
  DEFAULT_PREFERENCES,
  migrateLegacyMapState,
  readAtomMapState,
  writeAtomMapState,
} from './mapStateStorage';

type AtomMapProviderProps = {
  children: ReactNode;
};

type AtomMapContextValue = {
  preferences: AtomMapUiPreferences;
  dispatch: React.Dispatch<MapStateAction>;
};

const AtomMapContext = createContext<AtomMapContextValue>({
  preferences: DEFAULT_PREFERENCES,
  dispatch: () => {},
});

export const useAtomMap = (): AtomMapContextValue => useContext(AtomMapContext);

const initPreferences = (): AtomMapUiPreferences => {
  if (!atomMapStateExists()) {
    const migrated = migrateLegacyMapState();
    if (migrated) {
      return migrated;
    }
  }

  return readAtomMapState();
};

const AtomMapProvider = ({ children }: AtomMapProviderProps) => {
  const [preferences, dispatch] = useReducer(
    mapStateReducer,
    undefined,
    initPreferences,
  );

  useEffect(() => {
    writeAtomMapState(preferences);
  }, [preferences]);

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={GOOGLE_MAPS_LIBRARIES}>
      <AtomMapContext.Provider value={{ preferences, dispatch }}>
        {children}
      </AtomMapContext.Provider>
    </APIProvider>
  );
};

export default AtomMapProvider;
